import { inject } from 'inversify';
import { DateTime } from 'luxon';

import type { IUnitInfo } from '../../types/api/IUnitInfo';
import type { IUnitMsg } from '../../types/api/messages/IUnitMsg';
import type { IUnitsMsg } from '../../types/api/messages/IUnitsMsg';

import { ConfigurationManager } from './ConfigurationManager';
import { PostManager } from './PostManager';
import { TripManager } from './TripManager';

import { UNITS } from '@/constants';
import { service } from '@/decorators/service';
import { SignalrListener, SignalrMessage } from '@/decorators/signalr';
import { Unit } from '@/entities/Unit';
import CONSTANTS from '@/helpers/constants';
import { SpecialStatusManager } from '@/managers/SpecialStatusManager';
import { VehicleManager } from '@/managers/VehicleManager';
import { Logger } from '@/services/Logger';
import { UpdateUnit } from '@/services/signalr/MessageTypes/Units';
import { Signalr } from '@/services/signalr/Signalr';
import { UnitStore } from '@/stores/UnitStore';
import { isValidPosition } from '@/utils/isValidPosition';
import { messageOrderComparision } from '@/utils/messageOrderComparision';
import { normalize } from '@/utils/normalize';
import { orderByField } from '@/utils/orderBy';
import { sanitizeString } from '@/utils/sanitizeString';
import { IVehicleMovedMsg } from '~/api/messages/IVehicleMovedMsg';

@SignalrListener
@service(UnitManager)
export class UnitManager {
  @inject(Signalr) protected signalr: Signalr;
  @inject(Logger) protected logger: Logger;
  @inject(UnitStore) protected unitStore: UnitStore;
  @inject(SpecialStatusManager) protected specialStatusManager: SpecialStatusManager;
  @inject(PostManager) protected postManager: PostManager;
  @inject(TripManager) protected tripManager: TripManager;
  @inject(VehicleManager) protected vehicleManager: VehicleManager;
  @inject(ConfigurationManager) protected configurationManager: ConfigurationManager;

  protected loaded = false;
  private initialLoadTracked = false;

  get hasLoaded() {
    return this.loaded;
  }

  get all() {
    const units = this.unitStore.all();
    return orderByField(units, this.unitStore.valueKey);
  }

  get activeUnits() {
    const activeUnits = this.all.filter((unit) => unit.status !== UNITS.STATUS.INACTIVE.NAME);
    return orderByField(activeUnits, this.unitStore.valueKey);
  }

  get activeVehicles() {
    const activeVehicles = this.all.filter((unit) => unit.vehicle?.status !== UNITS.STATUS.INACTIVE.NAME);
    return orderByField(activeVehicles, this.unitStore.valueKey);
  }

  get isUnitConfigured() {
    const unit = this.configurationManager.find(CONSTANTS.CONFIGURATIONS.UNIT);
    return sanitizeString(`${unit?.value}`) === CONSTANTS.DATA_VALUES.YES;
  }

  find(vehicleId: string) {
    return this.all.find((unit) => unit.vehicle?.vehicleId === vehicleId);
  }

  findByUnitId(unitId: string) {
    return this.all.find((unit) => unit.unitId === unitId);
  }

  reset() {
    this.loaded = false;
    this.unitStore.set([]);
  }

  @SignalrMessage('UnitsMsg')
  protected async onUnitsMsg(message: IUnitsMsg) {
    const { sentTime } = message;
    const validUnits: Unit[] = [];

    message.units.forEach((unit) => {
      const existingVehicle = this.find(unit.vehicle?.vehicleId);

      if (messageOrderComparision(sentTime, existingVehicle?.sentTime)) {
        validUnits.push({ ...unit, sentTime } as Unit);
      }
      else {
        this.logger.captureMessage('Dropped message', { tags: { store: 'units', action: 'update' } });
      }
    });

    if (validUnits.length > 0) {
      this.update(validUnits);
    }

    //  Track initial resource list load time (first UnitsMsg after app boot)
    if (!this.initialLoadTracked) {
      this.initialLoadTracked = true;
      const appStartEntries = performance.getEntriesByName('app-start', 'mark') as PerformanceMark[];
      const appStartMs = appStartEntries.length > 0 ? appStartEntries[0].startTime : 0;
      this.logger.track('resource_list.initial_load', {
        durationMs: Math.round(performance.now() - appStartMs),
        unitCount: message.units?.length ?? 0,
      });
    }
  }

  @SignalrMessage('UnitMsg')
  protected async onUnitMsg(message: IUnitMsg) {
    const { sentTime } = message;
    const existingVehicle = this.find(message.unit?.vehicle?.vehicleId);
    if (messageOrderComparision(sentTime, existingVehicle?.sentTime)) {
      this.update([{ ...message.unit, sentTime }]);
    }
    else {
      this.logger.captureMessage('Dropped message', { tags: { store: 'units', action: 'update' } });
    }
  }

  update(units: Unit[]) {
    this.loaded = true;
    const normalizedUnits: Unit[] = [];

    units.forEach((unit) => {
      normalizedUnits.push(new Unit(unit));
      // add inactive vehicle to vehicles store
      if (unit.vehicle.status === UNITS.STATUS.INACTIVE.NAME) {
        this.vehicleManager.update(unit.vehicle);
      }
    });

    this.unitStore.batchUpdate(normalizedUnits);
  }

  async postUpdateReqMsg(vehicleId: string, postId: string, isAtPost = false) {
    await this.signalr.invoke(UpdateUnit, {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      contextKey: isAtPost ? CONSTANTS.UI.CONTEXTS.UNIT_AT_POST_UPDATE : CONSTANTS.UI.CONTEXTS.UNIT_POST_UPDATE,
      unit: {
        vehicle: {
          postId,
          vehicleId,
        },
      },
    });
  }

  async deactivateUnitReqMsg(unit: Partial<IUnitInfo>, endOdometer: string) {
    const response = await this.signalr.invoke(UpdateUnit, {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      contextKey: CONSTANTS.UI.CONTEXTS.DEACTIVATE_UNIT,
      unit: {
        status: '0',
        name: unit.name,
        vehicle: {
          vehicleId: unit.vehicle?.vehicleId,
          vehicleName: unit.vehicle?.vehicleName,
          status: '0',
          endOdometer,
        },
      },
    });

    return response;
  }

  async specialStatusUpdateReqMsg(
    unit: Partial<IUnitInfo>,
    specialStatusId: string,
    specialStatusComment: string,
    contextKey: string,
  ) {
    const response = await this.signalr.invoke(UpdateUnit, {
      contextKey,
      unit: {
        status: '1',
        name: unit.name,
        vehicle: {
          vehicleId: unit.vehicle?.vehicleId,
          vehicleName: unit.vehicle?.vehicleName,
          specialStatusId: `${specialStatusId}`,
          specialStatusComment,
          postId: '0',
          statusTimestamp: DateTime.now().toISO(),
        },
      },
    });

    return response;
  }

  async activateUnitReqMsg(unit: Partial<IUnitInfo>) {
    await this.signalr.invoke(UpdateUnit, {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      contextKey: CONSTANTS.UI.CONTEXTS.ACTIVATE_UNIT,
      unit,
    });
  }

  async updateUnitReqMsg(unit: Partial<IUnitInfo>) {
    await this.signalr.invoke(UpdateUnit, {
      contextKey: CONSTANTS.UI.CONTEXTS.UNIT_UPDATE,
      unit,
    });
  }

  async updateVehiclePosition(vehicleMovedMessage: IVehicleMovedMsg) {
    if (vehicleMovedMessage.avlType !== 0) {
      return;
    }

    if (!isValidPosition(vehicleMovedMessage.position)) {
      return;
    }

    const unitEntity = this.find(vehicleMovedMessage.id);
    if (!unitEntity?.vehicle) {
      return;
    }

    const existingPosition = unitEntity.vehicle.position;
    unitEntity.vehicle.position = existingPosition
      ? { ...existingPosition, ...vehicleMovedMessage.position }
      : { ...vehicleMovedMessage.position };

    unitEntity.vehicle.bearing = normalize(vehicleMovedMessage.bearing) ?? unitEntity.vehicle.bearing;
    unitEntity.vehicle.altitude = normalize(vehicleMovedMessage.altitude) ?? unitEntity.vehicle.altitude;
    unitEntity.vehicle.speed = normalize(vehicleMovedMessage.speed) ?? unitEntity.vehicle.speed;

    this.unitStore.update(unitEntity);
  }
}
